import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { Info, ThumbsUp, TriangleAlert } from 'lucide-react'
import { cn } from '~/lib/utils'

const alertVariants = cva(
	'relative flex w-full flex-row gap-3 rounded-lg border px-4 py-3 text-sm',
	{
		variants: {
			variant: {
				default: 'bg-card text-foreground',
				destructive:
					'border-destructive/50 bg-card text-destructive dark:border-destructive [&>svg]:text-destructive',
				warning:
					'border-warning/50 bg-card text-warning dark:border-warning [&>svg]:text-warning',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
)

const Alert = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
	<div
		ref={ref}
		role='alert'
		className={cn(alertVariants({ variant }), className)}
		{...props}
	/>
))

Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h5
		ref={ref}
		className={cn(
			'mt-0.5 mb-1 font-medium font-semibold leading-none tracking-tight',
			className,
		)}
		{...props}
	/>
))

AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn('text-sm leading-none [&_p]:leading-relaxed', className)}
		{...props}
	/>
))

AlertDescription.displayName = 'AlertDescription'

const AlertIcon = ({ type }: { type?: 'bad' | 'good' | 'neutral' }) => {
	if (type === 'bad') return <TriangleAlert className='size-4 shrink-0' />
	if (type === 'good') return <ThumbsUp className='size-4 shrink-0' />

	return <Info className='size-4 shrink-0' />
}

export { Alert, AlertDescription, AlertIcon, AlertTitle }
